import { SubmissionError } from '#helper/submission-error';
import {
  FETCH_PROJECTS_USERS_DEVICES,
  SUCCESS,
  CLOSE_BACKDROP
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { error, snackBarError, fetchJson } from '#action/fetch';
import { getRequestOptions } from '#helper';
import { history } from '#configureStore';

export function addProject(data) {
  return dispatch => fetch(webPortalApi.buildApiUrl('project/'), {
    method: 'POST',
    body: JSON.stringify(data),
    ...getRequestOptions()
  }).then(r => r.json().then(d => {
    dispatch({ type: CLOSE_BACKDROP });
    if (!r.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request',
          ...d
        })
      );
      throw new SubmissionError(d);
    }

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Project was created!'
      }
    });

    return d;
  }));
}

export const fetchProject = async id => {
  try {
    const params = new URLSearchParams({
      ids: id,
      modules: true
    });

    const url = webPortalApi.buildApiUrl(`project?${params.toString()}`);
    const [project] = await fetchJson(url);

    return project;
  } catch (err) {
    console.error(err);
    return {};
  }
};

const fetchProjectDevices = async (projectId, devices) => {
  if (devices?.length < 11) {
    const searchParams = new URLSearchParams({
      order: 'desc',
      orderBy: 'title',
      offset: 0,
      limit: 10,
      pagination: 1,
      projects: projectId,
      withoutYoungest: 1
    });
    const url = webPortalApi.buildApiUrl(`device/distinct/details?${searchParams.toString()}`);
    const res = await fetchJson(url);

    return res.devices;
  }
  return devices;
};

const fetchProjectUsers = async projectUsers => {
  const url = webPortalApi.buildApiUrl(`users?ids=${projectUsers}`);
  return fetchJson(url);
};

export const fetchLimitedProjects = async (id, { modules = true } = {}) => {
  const params = new URLSearchParams({
    order: 'ASC',
    orderBy: 'parent',
    limit: 100
  });

  if (id) {
    params.set('ids', id);
  }

  if (modules) {
    params.set('modules', true);
  }

  const url = webPortalApi.buildApiUrl(`project?${params.toString()}`);
  return fetchJson(url);
};

export const fetchProjectsUsersDevices = projectId => async dispatch => {
  try {
    let project = [];
    let devices = [];
    let users = [];
    let projectParentId = '';

    const params = new URLSearchParams({
      modules: true,
      belongings: 1,
      ...(projectId && { ids: projectId })
    });

    const projectUrl = webPortalApi.buildApiUrl(`project?${params.toString()}`);
    const [currentProject] = await fetchJson(projectUrl);

    if (projectId && !currentProject) {
      dispatch(
        snackBarError({
          message: 'You do not have access'
        })
      );

      await history.push('/project');
    }

    if (currentProject) {
      projectParentId = currentProject.parentId;
      devices = await fetchProjectDevices(projectId, currentProject.devices);
      users = currentProject.users ? await fetchProjectUsers(currentProject.users.join(',')) : [];
      project = [currentProject];
    }

    const restProjects = await fetchLimitedProjects(projectParentId) ?? [];

    dispatch({
      type: FETCH_PROJECTS_USERS_DEVICES,
      payload: {
        users,
        devices,
        projects: [...restProjects, ...project]
      }
    });
  } catch (err) {
    dispatch(error({ message: err }));
  }
};

export const putProject = (id, data) => dispatch =>
  fetch(webPortalApi.buildApiUrl(`project/${id}`), {
    method: 'PUT',
    body: JSON.stringify(data),
    ...getRequestOptions()
  }).then(r => r.json().then(d => {
    dispatch({ type: CLOSE_BACKDROP });
    if (!r.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request',
          ...d
        })
      );
      throw new SubmissionError(d);
    }

    return dispatch({
      type: SUCCESS,
      payload: {
        message: 'Project was updated!'
      }
    });
  }));

export const deleteProject = projectId => dispatch =>
  fetch(webPortalApi.buildApiUrl(`project/${projectId}`), {
    method: 'DELETE',
    ...getRequestOptions()
  }).then(r => r.json().then(d => {
    if (!r.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request',
          ...d
        })
      );
      throw new SubmissionError(d);
    }

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Project was deleted!'
      }
    });

    return { success: true };
  }));
